import { skipToken, useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { SilentError } from 'init/silent-error';
import { z } from 'zod/v4';

import {
  type ApiError,
  addResourceNameToQueryResult,
  handleZodError,
  type QueryOptions,
  useAxiosInstance,
} from './utils';

const TemplateBodySchema = z.object({
  id: z.coerce.string(),
  groupedType: z.string().nullable(),
  locale: z.string(),
  body: z.string().nullable(),
  subject: z.string().nullable(),
  fromName: z.string().nullable(),
  fromEmail: z.string().nullable(),
  replyToName: z.string().nullable(),
  replyToEmail: z.string().nullable(),
});

const UsedByResourceSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
});

export type UsedByResource = z.infer<typeof UsedByResourceSchema>;

const TemplateV2Schema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  stepType: z.string(),
  planType: z.string(),
  includePDF: z.boolean(),
  includeUbl: z.boolean(),
  updatedAt: z.string().transform(dayjs),
  usedBy: z.array(UsedByResourceSchema).default([]),
  noDeletionReason: z.string().nullable(),
  noChangeTypeReason: z.string().nullable(),
  noChannelDeletionReasons: z
    .object({
      email: z.string().nullable(),
      letter: z.string().nullable(),
    })
    .default({ email: null, letter: null }),
  bodies: z.object({
    email: z.array(TemplateBodySchema),
    letter: z.array(TemplateBodySchema),
  }),
});

export type TemplateV2 = z.infer<typeof TemplateV2Schema>;

export async function loadTemplateV2QueryFn(axiosInstance: Promise<AxiosInstance>, id: string) {
  const instance = await axiosInstance;
  const { data } = await instance.get(`/templates/${id}`);

  const parseResult = TemplateV2Schema.safeParse(data.data);
  if (!parseResult.success) {
    handleZodError(parseResult, 'template');
    throw new SilentError();
  }

  return parseResult.data;
}

export function useLoadTemplateV2(id: string | undefined, options: QueryOptions<TemplateV2> = {}) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<TemplateV2, ApiError>({
    queryKey: ['template-v2', id],
    queryFn: id === undefined ? skipToken : () => loadTemplateV2QueryFn(axiosInstance, id),
    ...options,
  });

  return addResourceNameToQueryResult<TemplateV2, ApiError, 'template'>('template', queryResult);
}
